import { IStringDictionary, StringDictionary } from "../../core/StringDictionary";
import { ProductComponent, IProductComponent } from "./ProductComponent";
import { ProductProperty, IProductProperty } from "./ProductProperty";

export enum ProductStatus {
    Active = 0,
    Retired = 1
}

export enum ProductType {
    DigitalGoods = 0,
    Inventory = 1,
    Bundle = 3,
    Service = 5,
    Fee = 6
}

export class Product implements IProduct {
    constructor(product?: IProduct, products?: Product[]) {
        // initialize typed object from generic object
        this.products = products;
        this.productNumber = product?.productNumber ?? "";
        this.title = product?.title ?? "";
        this.localizedTitles = product?.localizedTitles instanceof StringDictionary ? product?.localizedTitles : new StringDictionary(product?.localizedTitles);
        this.culture = product?.culture ?? "en-US";
        this.status = product?.status ?? ProductStatus.Active;
        this.productType = product?.productType ?? ProductType.DigitalGoods;
        this.unitType = product?.unitType;
        this.defaultUnitCode = product?.defaultUnitCode;
        this.defaultPriceId = product?.defaultPriceId ?? 0;
        this.listPrice = product?.listPrice ?? 0;
        this.currencyCode = product?.currencyCode ?? "USD";
        this.fulfillmentProvider = product?.fulfillmentProvider;
        this.familyCode = product?.familyCode;
        this.thumbnailUrl = product?.thumbnailUrl;
        this.informationUrl = product?.informationUrl;
        this.weight = product?.weight;
        this.isbn = product?.isbn;
        this.courseId = product?.courseId;
        this.meritItemId = product?.meritItemId;
        this.components = product?.components?.map(component => new ProductComponent(component, this)) ?? [];
        this.properties = product?.properties?.map(property => new ProductProperty(property, this)) ?? [];
        this.dictionary = product?.dictionary instanceof StringDictionary ? product?.dictionary : new StringDictionary(product?.dictionary);
    }

    toJSON(): object {
        // return generic object with properties to stringify
        return {
            productNumber: this.productNumber,
            title: this.title,
            localizedTitles: this.localizedTitles,
            culture: this.culture === "en-US" ? undefined : this.culture,
            status: this.status,
            productType: this.productType,
            unitType: this.unitType,
            defaultUnitCode: this.defaultUnitCode,
            defaultPriceId: this.defaultPriceId,
            listPrice: this.listPrice,
            currencyCode: this.currencyCode === "USD" ? undefined : this.currencyCode,
            fulfillmentProvider: this.fulfillmentProvider,
            familyCode: this.familyCode,
            thumbnailUrl: this.thumbnailUrl,
            informationUrl: this.informationUrl,
            weight: this.weight,
            isbn: this.isbn,
            courseId: this.courseId,
            meritItemId: this.meritItemId,
            components: this.components.length > 0 ? this.components : undefined,
            properties: this.properties.length > 0 ? this.properties : undefined,
            dictionary: this.dictionary.length > 0 ? this.dictionary : undefined
        };
    }

    productNumber: string;
    title: string;
    localizedTitles?: StringDictionary;
    culture: string;
    status: ProductStatus;
    productType: ProductType;
    unitType?: string;
    defaultUnitCode?: string;
    defaultPriceId: number;
    listPrice: number;
    currencyCode?: string;
    fulfillmentProvider?: string;
    familyCode?: string;
    thumbnailUrl?: string;
    informationUrl?: string;
    weight?: number;
    isbn?: string;
    courseId?: number;
    meritItemId?: string;
    components: ProductComponent[];
    properties: ProductProperty[];
    dictionary: StringDictionary;

    // internal
    products?: Product[];

    //Begin Calculated Properties

    get hasUserProperty(): boolean {
        return this.properties.some(property => property.key === "User");
    }

    get optionProperties(): ProductProperty[] {
        return this.properties.filter(property => property.key !== "User");
    }

    get courseIdLabel() {
        return this.dictionary.containsKey("CourseId") ? this.dictionary.get("CourseId") : "";
    }

    get meritItemIdLabel() {
        return this.dictionary.containsKey("MeritItemId") ? this.dictionary.get("MeritItemId") : "";
    }

    get defaultPriceIdLabel() {
        return this.dictionary.containsKey("DefaultPriceID") ? this.dictionary.get("DefaultPriceId") : "";
    }

    //End Calculated Properties

    getLocalizedTitle(culture: string) {
        if (this.localizedTitles) {
            if (this.localizedTitles.containsKey(culture))
                return this.localizedTitles.get(culture);
        }
        return this.title;
    }
}   

export interface IProduct {
    productNumber: string;
    title: string;
    localizedTitles?: IStringDictionary;
    culture?: string;
    status: ProductStatus;
    productType: ProductType;
    unitType?: string;
    defaultUnitCode?: string;
    defaultPriceId?: number;
    listPrice?: number;
    currencyCode?: string;
    fulfillmentProvider?: string;
    familyCode?: string;
    thumbnailUrl?: string;
    informationUrl?: string;
    weight?: number;
    isbn?: string;
    courseId?: number;
    meritItemId?: string;
    components?: IProductComponent[];
    properties?: IProductProperty[];
    dictionary?: IStringDictionary;
}